import { TrackToolService } from '@analytics/events';
import type {
  ParsedToolTrackingConfig,
  TrackToolStep,
} from '@analytics/models';
import { Component, Input, type OnInit } from '@angular/core';
import type { ScMultiListField } from '@core/jss-models';
import {
  getFieldValue,
  getItemLinkFieldValue,
  getTreeListValues,
} from '@core/jss-utils';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  SIMPLE_PROGRESSIVE_FORMS_CONFIG,
  SimpleProgressiveFormsConfigService,
  type SimpleProgressiveFormCardContent,
} from '@innogy/common-ui/progressive-forms';
import {
  addCheckboxFormControlAction,
  type FormConfirmation,
} from '@innogy/feh/shared';
import type { ProgressiveFormGroupState } from '@innogy/progressive-ngrx-forms';
import type { ScCheckboxFormInput } from '@innogy/sitecore-forms/models';
import { Store } from '@ngrx/store';
import throttle from 'lodash/throttle';
import { validate, type FormControlState } from 'ngrx-forms';
import { requiredTrue } from 'ngrx-forms/validation';

import type { SolarPanelOrderFormToolTrackingSteps } from '../../store';
import { SolarPanelsOrderFormRendering } from '../../models/solar-panels-order-form-rendering.interface';

@Component({
  selector:
    'wl-solar-panels-order-form[rendering][solarPanelsOrderFormViewModel][trackToolConfig]',
  templateUrl: './solar-panels-order-form.component.html',
  styleUrls: ['./solar-panels-order-form.component.scss'],
  providers: [
    {
      provide: SIMPLE_PROGRESSIVE_FORMS_CONFIG,
      useValue: {
        scrollIntoViewWhenActive: true,
        enableAutomaticFocusing: true,
        totalStepsInFlow: 2,
      },
    },
    SimpleProgressiveFormsConfigService,
  ],
})
export class SolarPanelsOrderFormComponent implements OnInit {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering!: SolarPanelsOrderFormRendering;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() solarPanelsOrderFormViewModel!: any;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input()
  trackToolConfig!: ParsedToolTrackingConfig<SolarPanelOrderFormToolTrackingSteps>;

  confirmationCardContent?: SimpleProgressiveFormCardContent;
  moreInfoText = 'Meer informatie';
  showMoreInfo = false;

  constructor(
    private readonly trackToolService: TrackToolService,
    private readonly store$: Store
  ) {}

  completeTool = throttle(
    () => this.trackToolService.completeTool(this.trackToolConfig),
    2000,
    { trailing: false }
  );

  get confirmationCheckboxes(): ScMultiListField<ScCheckboxFormInput> {
    return getTreeListValues(this.rendering.fields, 'ConfirmationCheckboxes');
  }

  ngOnInit(): void {
    this.confirmationCardContent = {
      title: getFieldValue(this.rendering.fields, 'ConfirmationStepTitle', ''),
      subtitle: getFieldValue(
        this.rendering.fields,
        'ConfirmationStepSubTitle',
        ''
      ),
      submitButtonText: getFieldValue(
        this.rendering.fields,
        'ConfirmationStepSubmitButtonText',
        ''
      ),
      submitButtonIcon: getItemLinkFieldValue(
        this.rendering.fields,
        'ConfirmationStepSubmitButtonIcon'
      ) as IconProp,
      stepNumber: this.confirmTrackStep.step,
    };

    this.confirmationCheckboxes.forEach((checkbox) => {
      const name = getFieldValue<string>(checkbox.fields, 'Label');
      const required = !!getFieldValue<boolean>(checkbox.fields, 'Required');

      if (name) {
        // Add the checkbox formControls & validators dynamically
        this.store$.dispatch(
          addCheckboxFormControlAction({
            name,
            ...(required && { validator: validate(requiredTrue) }),
          })
        );
      }
    });
  }

  get confirmTrackStep(): TrackToolStep {
    return this.trackToolConfig.forStep('controleren');
  }

  getControlByLabel(
    label: string | undefined,
    state?: ProgressiveFormGroupState<FormConfirmation>
  ): FormControlState<boolean> | undefined {
    if (!label || !state) {
      return undefined;
    }

    return state.formState.controls.checkboxes.controls[label];
  }
}
